import React, { useCallback, useEffect, useState } from 'react';
import { Bot, Check, FileText, Glasses, Hammer, HelpCircle, Inbox, Loader2, MessageSquare, Pencil, Presentation, X } from 'lucide-react';
import { queueService, QueueEntry, QueueKind, R1aMode } from '../../services/queueService';

const POLL_MS = 10_000;

const KIND: Record<QueueKind, { label: string; icon: React.ElementType }> = {
  contribution: { label: 'Contribution', icon: MessageSquare },
  'build-hint': { label: 'Build hint', icon: Hammer },
  deliverable: { label: 'Agent output', icon: FileText },
  ask: { label: 'Ask', icon: HelpCircle },
  'router-prompt': { label: 'Prompt', icon: Bot },
  'register-proposal': { label: 'Register', icon: Bot },
  'slide-brief': { label: 'Slide brief', icon: Presentation },
};

const WHEN_LABEL: Record<string, string> = { now: 'now', 'next-break': 'next break', 'wrap-up': 'wrap-up' };

const ANSWER_LABEL: Record<string, string> = { accept: 'Accept', ignore: 'Ignore', edit: 'Edit' };

const time = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

const keyOf = (e: Pick<QueueEntry, 'kind' | 'ref'>) => `${e.kind}:${e.ref}`;

interface Props {
  sessionId: string;
  canEdit?: boolean;
}

/** Everything waiting for the facilitator (agentic core R1a, WS3). */
const QueuePanel: React.FC<Props> = ({ sessionId, canEdit = true }) => {
  const [items, setItems] = useState<QueueEntry[]>([]);
  const [mode, setMode] = useState<R1aMode>('shadow');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ key: string; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const view = await queueService.list(sessionId);
      setItems(Array.isArray(view?.items) ? view.items : []);
      setMode(view?.mode ?? 'shadow');
    } catch {
      /* keep the last list; the next poll retries */
    } finally {
      setLoaded(true);
    }
  }, [sessionId]);

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const answer = async (entry: QueueEntry, choice: string, text?: string) => {
    const key = keyOf(entry);
    setBusy(key);
    setError(null);
    try {
      await queueService.answer(sessionId, entry, choice, text);
      setItems((prev) => prev.filter((i) => keyOf(i) !== key));
      setEditing(null);
    } catch (e: any) {
      const status = e?.response?.status;
      setError(status === 409 ? 'Already answered elsewhere.' : e?.response?.data?.error || 'Could not send the answer');
      if (status === 409 || status === 404) setItems((prev) => prev.filter((i) => keyOf(i) !== key));
    } finally {
      setBusy(null);
    }
  };

  if (mode === 'off') return null;

  return (
    <div className="flex flex-col gap-2 text-left" data-testid="queue-panel">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-bold text-primary tracking-widest uppercase">Queue{items.length ? ` (${items.length})` : ''}</span>
        {mode === 'shadow' && (
          <span className="px-2 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[10px] font-semibold" title="Shown here only: nothing goes to the glasses and no default is applied">
            Shadow
          </span>
        )}
      </div>
      {error && <p className="text-xs text-red-600" role="alert">{error}</p>}

      {!loaded ? (
        <div className="flex justify-center py-4 text-slate-400"><Loader2 size={16} className="animate-spin" /></div>
      ) : items.length === 0 ? (
        <p className="flex items-center gap-1.5 text-xs text-slate-400"><Inbox size={14} /> Nothing waiting.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-[#e2e8f0] border border-[#e2e8f0] rounded-xl bg-white max-h-96 overflow-y-auto" aria-label="Queue">
          {items.map((entry) => {
            const key = keyOf(entry);
            const kind = KIND[entry.kind] ?? KIND.ask;
            const Icon = kind.icon;
            const isAsk = entry.kind === 'ask' || entry.kind === 'slide-brief' || !!entry.question;
            const isEditing = editing?.key === key;
            return (
              <li key={key} className="flex flex-col gap-2 px-3 py-2.5">
                <div className="flex items-start gap-2">
                  <Icon size={14} className="text-slate-400 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-700 line-clamp-3" title={entry.summary}>{entry.summary}</p>
                    {entry.brief && (
                      <div className="mt-1">
                        <ul className="list-disc pl-4 text-[11px] text-slate-600">
                          {entry.brief.points.map((p, i) => <li key={i}>{p.text}</li>)}
                        </ul>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {entry.brief.slideType} · {entry.brief.screen} screen · {WHEN_LABEL[entry.brief.when] ?? entry.brief.when}
                        </p>
                      </div>
                    )}
                    <p className="flex items-center gap-1 text-[10px] text-slate-400 mt-0.5">
                      {kind.label} · {time(entry.createdAt)}
                      {entry.expiresAt && isAsk && <> · {entry.default} at {time(entry.expiresAt)}</>}
                      {entry.onGlasses && <Glasses size={11} className="text-primary" aria-label="Also on the glasses" />}
                    </p>
                  </div>
                </div>

                {canEdit && (isEditing ? (
                  <div className="flex items-center gap-2 pl-5">
                    <input
                      value={editing!.text}
                      onChange={(e) => setEditing({ key, text: e.target.value })}
                      aria-label="Edited change"
                      className="flex-1 border border-[#e2e8f0] rounded-lg px-2 py-1 text-xs text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                    <button type="button" disabled={busy === key || !editing!.text.trim()} onClick={() => answer(entry, 'edit', editing!.text.trim())}
                      aria-label="Save and accept" className="h-6 w-6 flex items-center justify-center rounded-md bg-primary text-white disabled:bg-slate-100 disabled:text-slate-400">
                      <Check size={12} />
                    </button>
                    <button type="button" onClick={() => setEditing(null)} aria-label="Cancel edit" className="h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-50">
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5 pl-5">
                    {entry.answers.map((choice) => (
                      <button
                        key={choice}
                        type="button"
                        disabled={busy === key}
                        onClick={() => (choice === 'edit' && !isAsk ? setEditing({ key, text: entry.summary }) : answer(entry, choice))}
                        className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors disabled:opacity-50 ${
                          choice === 'accept' || (isAsk && choice === entry.default)
                            ? 'bg-primary/10 text-navy border-primary/30 hover:bg-primary/20'
                            : 'bg-white text-slate-500 border-[#e2e8f0] hover:border-slate-300'
                        }`}
                      >
                        {choice === 'edit' && !isAsk && <Pencil size={10} />}
                        {isAsk ? choice : ANSWER_LABEL[choice] ?? choice}
                      </button>
                    ))}
                    {busy === key && <Loader2 size={12} className="animate-spin text-slate-400" />}
                  </div>
                ))}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default QueuePanel;
